import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileQuestion } from 'lucide-react';
import { rootRoute } from 'routes/routes';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type NotFoundProps = Readonly<{
  /**
   * True when a route guard renders this inside the sidebar chrome, in place of a subtree the user
   * cannot reach, rather than as the standalone catch-all route. It swaps the outer container for
   * one that fits `Home`'s content pane -- nothing inside it changes, which is the point: a route
   * withheld for lack of entitlement must read exactly like a URL that was never a route.
   */
  inShell?: boolean;
}>;

const NotFound = ({ inShell = false }: NotFoundProps): JSX.Element => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <div className={cn('flex flex-col items-center justify-center px-4', inShell ? 'flex-1' : 'min-h-screen bg-muted')}>
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="rounded-full bg-muted-foreground/10 p-6">
          <FileQuestion className="h-12 w-12 text-muted-foreground" />
        </div>

        <div className="space-y-2">
          <p className="text-6xl font-bold text-muted-foreground/40">404</p>
          <h1 className="font-serif text-2xl leading-8">{t('not-found-title', 'Page not found')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('not-found-description', "The page you're looking for doesn't exist or has been moved.")}
          </p>
        </div>

        <Button onClick={() => navigate(rootRoute)} className="w-full max-w-xs">
          {t('not-found-go-home', 'Go Home')}
        </Button>
      </div>
    </div>
  );
};

export default NotFound;
